import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

import classes from "./BetaRequestModal.scss";
import classNames from "classnames";
import { useI18n } from "Hooks";
import { Modal, Button, Checkbox, IconButton } from "@toddle-edu/ds-web";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import _ from "lodash";
import { productRequest } from "../../modules/ProductUpdatesV2Modules";
import { getOrganizationId } from "modules/CommonSelectors";
import {
  getModuleIcon,
  getFeatureAvailableForRequest,
} from "../../modules/ProductUpdatesV2Utils";

const betaRequestModalStyle = {
  padding: "0px",
};

const BetaRequestModal = props => {
  const {
    testId,
    setShowBetaRequestModal,
    productUpdateId,
    groupedProductUpdateItems,
    masterRequest = false,
    productRequest,
    organizationId,
    productUpdates,
  } = props;
  const { t } = useI18n(["common"]);

  const [selectedReleaseItems, setSelectedReleaseItems] = useState([]);
  const [betaRequestEnabledCount, setBetaRequestEnabledCount] = useState(0);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const initialFiltersRef = useRef(null);
  const shiftEnterShortcut = { code: "Enter", shiftKey: true };

  const isBetaRequestDisabled = betaRequestStatus => {
    return betaRequestStatus === "DONE" || betaRequestStatus === "REQUESTED";
  };

  useEffect(() => {
    const selectedReleaseItemIds = [];
    let betaRequestEnabledCount = 0;
    _.map(groupedProductUpdateItems, ({ releaseItems }) => {
      const { betaRequestStatus } = _.first(releaseItems);
      const isDisabled = isBetaRequestDisabled(betaRequestStatus);
      if (betaRequestStatus) {
        betaRequestEnabledCount += _.size(releaseItems);
        if (!isDisabled) {
          const releaseItemsIds = _.map(releaseItems, releaseItem =>
            _.get(releaseItem, "id")
          );
          selectedReleaseItemIds.push(...releaseItemsIds);
        }
      }
    });
    const uniqueSelectedReleaseItemIds = _.uniq(selectedReleaseItemIds);
    initialFiltersRef.current = uniqueSelectedReleaseItemIds;
    setBetaRequestEnabledCount(betaRequestEnabledCount);
    setSelectedReleaseItems(uniqueSelectedReleaseItemIds);
  }, []);

  useEffect(() => {
    const filtersChanged = !_.isEqual(
      selectedReleaseItems,
      initialFiltersRef.current
    );
    setFiltersChanged(filtersChanged);
  }, [selectedReleaseItems]);

  const closeBetaRequestModal = () => {
    setShowBetaRequestModal(false);
  };

  const sendRequest = async () => {
    const productRequestInput = {
      organizationId,
      productUpdateId,
      releaseItemIds: selectedReleaseItems,
    };
    const filtered = _.filter(groupedProductUpdateItems, ({ releaseItems }) =>
      _.some(releaseItems, releaseItem =>
        _.includes(selectedReleaseItems, _.get(releaseItem, "id"))
      )
    );

    // Check if all selected items are available on request type
    const isFeatureAvailableForRequest =
      !_.isEmpty(selectedReleaseItems) &&
      _.every(filtered, ({ releaseItems }) =>
        getFeatureAvailableForRequest({ releaseItems })
      );

    const isSuccess = await productRequest({
      productRequestInput,
      isFeatureAvailableForRequest,
    });
    if (isSuccess) {
      closeBetaRequestModal();
    }
    await productUpdates?.refetch();
  };

  const getCheckBoxValue = () => {
    let releaseItemCount = 0;
    let selectableItemCount = 0;
    _.map(groupedProductUpdateItems, ({ releaseItems }) => {
      const { betaRequestStatus } = _.first(releaseItems);
      const isDisabled = isBetaRequestDisabled(betaRequestStatus);
      releaseItemCount += _.size(releaseItems);
      if (!isDisabled) {
        selectableItemCount += _.size(releaseItems);
      }
    });
    const isAllChecked =
      selectableItemCount > 0 &&
      selectableItemCount === _.size(selectedReleaseItems);
    const isAllCheckboxDisabled = releaseItemCount === betaRequestEnabledCount;
    return { isAllChecked, isAllCheckboxDisabled };
  };

  const { isAllChecked, isAllCheckboxDisabled } = getCheckBoxValue();

  const toggleMasterCheckBoxHandler = event => {
    const newSelectedReleaseItems = [];
    const value = event?.target?.checked;
    if (value) {
      _.map(groupedProductUpdateItems, ({ releaseItems }) => {
        const { betaRequestStatus } = _.first(releaseItems);
        const isDisabled = isBetaRequestDisabled(betaRequestStatus);
        if (!isDisabled) {
          const releaseItemsIds = _.map(releaseItems, releaseItem =>
            _.get(releaseItem, "id")
          );
          newSelectedReleaseItems.push(...releaseItemsIds);
        }
      });
    }
    setSelectedReleaseItems(_.uniq(newSelectedReleaseItems));
  };

  return (
    <Modal width="800px" height={"fit-content"} dsVersion="2.0">
      <Modal.Header
        isClosable={false}
        hasSeparator={true}
        style={betaRequestModalStyle}
      >
        <div className={classes.headerContainer}>
          <h6>{t("common:request_for_beta_features")}</h6>
          <div className={classes.iconContainer}>
            <IconButton
              variant="plain"
              onClick={closeBetaRequestModal}
              icon={<CloseOutlined variant="subtle" size="xx-small" />}
              size={"x-small"}
            />
          </div>
        </div>
      </Modal.Header>
      <Modal.Body style={betaRequestModalStyle} isScrollable={true}>
        <div className={classNames("text-body", classes.modalBodyStyle)}>
          {masterRequest ? (
            <div className={classes.mainContainer}>
              <div className={classes.masterContainer}>
                <Checkbox
                  isChecked={isAllChecked}
                  onChange={toggleMasterCheckBoxHandler}
                  dsVersion="2.0"
                  testId={`${testId}-betaRequestModal-checkbox`}
                  disabled={isAllCheckboxDisabled}
                />
                <div className={"text-label-l"}>
                  {t("common:request_beta_features")}
                </div>
              </div>
            </div>
          ) : null}
          {_.map(groupedProductUpdateItems, ({ releaseItems }) => {
            const { module, version, id, betaRequestStatus } = _.first(
              releaseItems
            );
            const betaRequestValue = _.includes(selectedReleaseItems, id);
            const betaRequestEnabled = betaRequestStatus === "DONE";
            const betaRequestRequested = betaRequestStatus === "REQUESTED";
            const { icon, color } = getModuleIcon({
              moduleKey: _.get(module, "moduleKey"),
            });

            const toggleCheckBoxHandler = event => {
              let newSelectedReleaseItems;
              const value = event?.target?.checked;
              const releaseItemsIds = _.map(releaseItems, releaseItem =>
                _.get(releaseItem, "id")
              );
              if (value) {
                newSelectedReleaseItems = [
                  ...selectedReleaseItems,
                  ...releaseItemsIds,
                ];
              } else {
                newSelectedReleaseItems = _.filter(
                  selectedReleaseItems,
                  selectedReleaseItemId =>
                    !_.includes(releaseItemsIds, selectedReleaseItemId)
                );
              }
              setSelectedReleaseItems(newSelectedReleaseItems);
            };

            const isFeatureAvailableForRequest = getFeatureAvailableForRequest({
              releaseItems,
            });

            return (
              <div className={classes.moduleCheckboxContainer}>
                <div className={classes.itemTagsContainer}>
                  <Checkbox
                    isChecked={betaRequestValue}
                    onChange={toggleCheckBoxHandler}
                    dsVersion="2.0"
                    disabled={betaRequestEnabled || betaRequestRequested}
                    testId={`${testId}-betaRequestModal-checkbox`}
                  />
                  <div
                    className={classes.iconStyle}
                    style={{ backgroundColor: color }}
                  >
                    {icon}
                  </div>
                  <div className={"text-label-l"}>{_.get(module, "label")}</div>
                  {betaRequestRequested ? (
                    <div
                      className={classNames(
                        "text-body-l",
                        classes.requestTextStyle
                      )}
                    >
                      {t(
                        isFeatureAvailableForRequest
                          ? "common:already_requested"
                          : "common:already_requested_for_beta"
                      )}
                    </div>
                  ) : betaRequestEnabled ? (
                    <div
                      className={classNames(
                        "text-body-l",
                        classes.enabledTextStyle
                      )}
                    >
                      {t(
                        isFeatureAvailableForRequest
                          ? "common:enabled_for_your_school"
                          : "common:successfully_enabled_beta_version_for_your_school"
                      )}
                    </div>
                  ) : null}
                </div>
                <ul className={classes.listStyle}>
                  {_.map(releaseItems, releaseItem => {
                    return (
                      <li
                        className={classNames(
                          "text-body-l",
                          classes.categoryItem
                        )}
                      >
                        {_.get(releaseItem, "title")}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer style={betaRequestModalStyle}>
        <div className={classes.footerContainer}>
          <Button
            variant="neutral"
            type="outlined"
            size="medium"
            onClick={closeBetaRequestModal}
            dsVersion="2.0"
            testId={`${testId}-cancel`}
          >
            {t("common:cancel")}
          </Button>
          <Button
            size="medium"
            variant="primary"
            type="fill"
            dsVersion="2.0"
            onClick={sendRequest}
            disabled={!filtersChanged}
            testId={`${testId}-done`}
            shortcut={shiftEnterShortcut}
          >
            {t("common:send_request")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapDispatchToProps = {
  productRequest,
};

const mapStateToProps = (state, ownProps) => {
  const organizationId = getOrganizationId(state);
  return {
    organizationId,
  };
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  BetaRequestModal
);
